/**
 * TRUST ENTITIES (Phase 3, new). The legal Special Purpose Trusts that HOLD tokenized assets.
 *
 * This is the bridge the whole tokenization model turns on (ADR 0005): a token is an interest in a
 * TRUST that holds the asset, never in the asset itself. Land is transferred by the land register, and
 * a token is not the land register. So before fractions of an asset can be sold, a trust must exist,
 * hold the asset, and issue tokens against it. This surface is where a user sees and manages those
 * trusts.
 */

import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { ErrorState, LoadingCard } from '@/components/states';
import { useResource } from '@/lib/use-resource';
import { Reveal, Stagger, StaggerItem } from '@/components/motion';

export default function Trusts() {
  const { data, loading, error, reload } = useResource(() => api.trusts(), []);
  const trusts = data?.trusts ?? [];

  if (error) return <ErrorState body="We could not load your trusts." onRetry={reload} />;

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-page-title text-ink">Trust entities</h1>
          <p className="mt-0.5 text-sm text-ink-muted">
            The legal structures that hold your tokenized assets.
          </p>
        </div>
        <Link
          to="/trust/new"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-field bg-primary px-3.5 py-2 text-sm font-medium text-white transition hover:bg-primary-light"
        >
          + Register
        </Link>
      </header>

      {/* What a trust is. This is not decoration: the legal basis is the product. */}
      <Reveal>
        <section className="rounded-card border border-primary/20 bg-primary-tint p-4">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-brand">
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.8}
                  d="M12 3 4 6v5c0 4.5 3.2 8 8 10 4.8-2 8-5.5 8-10V6l-8-3Z"
                />
              </svg>
            </span>
            <p className="text-sm leading-relaxed text-ink">
              A token is an interest in a{' '}
              <strong className="font-medium">trust that holds the asset</strong>, not in the asset
              itself. The trust owns the land, property or equipment and issues tokenized shares
              against it, so ownership is recorded, enforceable, and independent of the platform.
            </p>
          </div>
        </section>
      </Reveal>

      {loading ? (
        <div className="grid gap-4 md:grid-cols-2">
          <LoadingCard lines={3} />
          <LoadingCard lines={3} />
        </div>
      ) : trusts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border-strong bg-surface p-10 text-center">
          <h2 className="font-semibold text-ink">No trusts yet</h2>
          <p className="mx-auto mt-1 max-w-sm text-sm text-ink-muted">
            To sell fractions of an asset, first register the trust that holds it.
          </p>
          <Link
            to="/trust/new"
            className="mt-5 inline-flex rounded-field bg-primary px-4 py-2.5 text-sm font-medium text-white shadow-soft transition hover:bg-primary-light"
          >
            Register a trust
          </Link>
        </div>
      ) : (
        <Stagger className="grid gap-4 md:grid-cols-2">
          {trusts.map((t) => (
            <StaggerItem key={t.id}>
              <Link
                to={`/trust/${t.id}`}
                className="flex h-full flex-col rounded-card border border-border bg-surface p-5 shadow-card transition hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-soft"
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-semibold text-ink">{t.legal_name}</h3>
                  <span className="shrink-0 rounded-pill bg-accent/15 px-2 py-0.5 text-micro font-medium capitalize text-accent">
                    {t.status}
                  </span>
                </div>
                <p className="mt-1 text-sm text-ink-muted">{t.asset}</p>

                <dl className="mt-4 grid grid-cols-2 gap-3 border-t border-border pt-3 text-sm">
                  <div>
                    <dt className="text-xs text-ink-subtle">Trustee</dt>
                    <dd className="truncate font-medium text-ink">{t.trustee}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-ink-subtle">Valuation</dt>
                    <dd className="tabular font-medium text-ink">KES {t.valuation}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-ink-subtle">Registration</dt>
                    <dd className="truncate font-mono text-xs text-ink">{t.registration_number}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-ink-subtle">Holders</dt>
                    <dd className="font-medium text-ink">{t.holders}</dd>
                  </div>
                </dl>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      )}
    </div>
  );
}
